import React, { useState, useCallback } from 'react';
import styled, { keyframes, useTheme } from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../LanguageContext';
import logoImage from '../assets/images/eco-logo.png';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  width: 100%;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  z-index: 1000;
`;

const Nav = styled.nav`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0.5rem 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  gap: 1rem;
`;

const LogoLink = styled(Link)`
  text-decoration: none;
`;

const Logo = styled.img`
  height: 80px;
  user-select: none;
`;

const Menu = styled.ul`
  list-style: none;
  display: flex;
  gap: 2rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

const MenuItem = styled.li`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.grayDark};
  cursor: pointer;
  transition: color 0.3s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.blueLight};
  }

  &:focus {
    outline: none;
    color: ${({ theme }) => theme.colors.blueLight};
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateX(20px); }
  to { opacity: 1; transform: translateX(0); }
`;

const HamburgerButton = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  margin-left: 1rem;
  border-radius: 4px;
  z-index: 1100;

  &:hover {
    background: ${({ theme }) => theme.colors.grayLight};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: block;
  }
`;

const HamburgerBox = styled.div`
  width: 24px;
  height: 18px;
  position: relative;
`;

const HamburgerInner = styled.div`
  background-color: ${({ theme }) => theme.colors.greenPrimary};
  position: absolute;
  width: 24px;
  height: 3px;
  border-radius: 2px;
  transition: all 0.3s ease;

  &:nth-child(1) {
    top: ${({ open }) => (open ? '7.5px' : '0')};
    transform: ${({ open }) => (open ? 'rotate(45deg)' : 'none')};
  }
  &:nth-child(2) {
    top: 7.5px;
    opacity: ${({ open }) => (open ? 0 : 1)};
  }
  &:nth-child(3) {
    top: ${({ open }) => (open ? '7.5px' : '15px')};
    transform: ${({ open }) => (open ? 'rotate(-45deg)' : 'none')};
  }
`;

const MobileMenu = styled.ul`
  list-style: none;
  position: fixed;
  top: 100px;
  right: 1rem;
  background: ${({ theme }) => theme.colors.white};
  width: 250px;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  display: ${({ open }) => (open ? 'block' : 'none')};
  animation: ${fadeIn} 0.3s ease forwards;
  z-index: 1100;

  li {
    margin-bottom: 1.25rem;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.grayDark};
    cursor: pointer;
    transition: color 0.3s ease;

    &:last-child {
      margin-bottom: 0;
      padding-top: 0.5rem;
      border-top: 1px solid ${({ theme }) => theme.colors.grayLight};
    }

    &:hover {
      color: ${({ theme }) => theme.colors.blueLight};
    }
  }
`;


const MobileAdminButton = styled(Link)`
  display: block;
  color: ${({ theme }) => theme.colors.greenPrimary};
  font-weight: bold;
  text-decoration: none;
  padding: 0.5rem 0;
  
  &:hover {
    color: ${({ theme }) => theme.colors.blueLight};
  }
`;

const AdminButton = styled(Link)`
  background-color: ${({ theme }) => theme.colors.greenPrimary};
  color: ${({ theme }) => theme.colors.white};
  padding: 0.5rem 1rem;
  border-radius: 4px;
  text-decoration: none;
  font-weight: 500;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.blueLight};
  }
`;

const LanguageSelector = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-left: 1rem;
  align-items: center;
  user-select: none;
`;

const FlagButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.5rem;
  opacity: ${({ active }) => (active ? 1 : 0.6)};
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 1;
  }
`;

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const { language, setLanguage, translations } = useLanguage();
  const navigate = useNavigate();

  const handleNavigation = useCallback((item) => {
    const sectionMap = {
      'Soluções': 'solutions',
      'Solutions': 'solutions',
      'Soluciones': 'solutions',
      'Como Funciona': 'howItWorks',
      'How It Works': 'howItWorks',
      'Cómo Funciona': 'howItWorks',
      'Blog': 'blog',
      'Responsabilidade': 'esg',
      'Responsibility': 'esg',
      'Responsabilidad': 'esg',
      'Contato': 'contact',
      'Contact': 'contact',
      'Contacto': 'contact'
    };

    // First navigate to home
    navigate('/');

    // Then scroll to section if it exists
    const sectionId = sectionMap[item];
    if (sectionId) {
      setTimeout(() => {
        const section = document.getElementById(sectionId);
        if (section) {
          section.scrollIntoView({ behavior: 'smooth' });
          setMenuOpen(false);
        }
      }, 100);
    }
  }, [navigate]);

  const navItems = translations.header.nav;

  return (
    <HeaderContainer>
      <Nav>
        <LogoLink to="/">
          <Logo src={logoImage} alt={translations.header.logo} />
        </LogoLink>
        <Menu>
          {navItems.map((item) => (
            <MenuItem 
              key={item} 
              onClick={() => handleNavigation(item)}
              onKeyPress={(e) => e.key === 'Enter' && handleNavigation(item)}
              tabIndex={0}
              role="button"
            >
              {item}
            </MenuItem>
          ))}
        </Menu>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <AdminButton to="/admin/login">Área Admin</AdminButton>
          <LanguageSelector>
          <FlagButton
            onClick={() => setLanguage('pt')}
            active={language === 'pt'}
            aria-label="Português do Brasil"
            title="Português do Brasil"
          >
            🇧🇷
          </FlagButton>
          <FlagButton
            onClick={() => setLanguage('en')}
            active={language === 'en'}
            aria-label="English"
            title="English"
          >
            🇺🇸
          </FlagButton>
          <FlagButton
            onClick={() => setLanguage('es')}
            active={language === 'es'}
            aria-label="Español"
            title="Español"
          >
            🇪🇸
          </FlagButton>
          </LanguageSelector>
        </div>
        <HamburgerButton
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <HamburgerBox>
            <HamburgerInner open={menuOpen} />
            <HamburgerInner open={menuOpen} />
            <HamburgerInner open={menuOpen} />
          </HamburgerBox>
        </HamburgerButton>
        <MobileMenu open={menuOpen} role="menu">
          {navItems.map((item) => (
            <li 
              key={item} 
              role="menuitem" 
              tabIndex={0} 
              onClick={() => {
                handleNavigation(item);
                setMenuOpen(false);
              }}
              onKeyPress={(e) => e.key === 'Enter' && handleNavigation(item)}
            >
              {item}
            </li>
          ))}
          <li>
            <MobileAdminButton 
              to="/admin/login" 
              onClick={() => setMenuOpen(false)}
            >
              Área Admin
            </MobileAdminButton>
          </li>
        </MobileMenu>
      </Nav>
    </HeaderContainer>
  );
};

export default Header;
